import { useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_MAX_RETRIES = 3
const RETRY_BASE_DELAY_MS = 500
// How long a URL that just exhausted its retries (or failed while offline) is
// skipped before a remount will attempt it again.
const RETRY_SUPPRESS_MS = 30_000
// Hard cap on the suppression memo so a very long browse session can't grow it
// without bound; entries are short-lived anyway (TTL + cleared on reconnect).
const SUPPRESS_MEMO_CAP = 1000

// ---------------------------------------------------------------------------
// Cross-instance failure suppression
// ---------------------------------------------------------------------------
// The card grid is virtualized (see ResourceGrid), so scrolling unmounts and
// remounts image tiles continuously. Each mount of useImageRetry starts with a
// fresh retry budget, so without this memo a persistently-broken image — or,
// worse, *every* image while offline — re-runs its full retry burst on every
// remount, producing a request storm (thousands of failed requests per page).
//
// A URL is recorded here when its burst is exhausted or it fails offline; a
// remounting tile consults the memo and goes straight to the fallback instead
// of re-bursting. The memo gates *mounts*, not live instances: a tile already
// mounted when a sibling with the same URL exhausts retries won't pick up the
// suppression until it remounts (harmless — at most one duplicate in-flight
// request). Entries expire after RETRY_SUPPRESS_MS so a transient failure is
// eventually re-attempted, and coming back online clears them immediately.
const suppressedUntil = new Map<string, number>()

function isSuppressed(url: string | null | undefined): boolean {
  if (!url) return false
  const until = suppressedUntil.get(url)
  if (until === undefined) return false
  if (until <= Date.now()) {
    suppressedUntil.delete(url)
    return false
  }
  return true
}

function suppress(url: string | null | undefined): void {
  if (!url) return
  if (suppressedUntil.size >= SUPPRESS_MEMO_CAP) {
    // Sweep expired entries first; only if still at the cap, evict the oldest
    // (Map preserves insertion order). Evicting one-by-one avoids wiping the
    // whole memo at once, which would reopen the burst for every still-broken
    // tile — the exact storm this guards against.
    const now = Date.now()
    for (const [key, until] of suppressedUntil) {
      if (until <= now) suppressedUntil.delete(key)
    }
    if (suppressedUntil.size >= SUPPRESS_MEMO_CAP) {
      const oldest = suppressedUntil.keys().next().value
      if (oldest !== undefined) suppressedUntil.delete(oldest)
    }
  }
  suppressedUntil.set(url, Date.now() + RETRY_SUPPRESS_MS)
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

// Reconnecting is the moment broken images become loadable again. A single
// module-level listener fans out to every mounted hook instance (see the
// `online` effect below) so reconnecting both drops suppression AND re-renders
// any tile currently latched to the fallback — clearing the module Map alone
// would not trigger a re-render, leaving already-mounted tiles (e.g. a
// non-virtualized grid or a standalone detail image) stuck until they remount.
const onlineSubscribers = new Set<() => void>()
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    suppressedUntil.clear()
    onlineSubscribers.forEach((notify) => notify())
  })
}

/**
 * Self-healing loader for external card images.
 *
 * Card images come from CDNs (TCGPlayer / OPTCG) and are cached by the service
 * worker as *opaque* responses. A transient CDN error — or a stale/broken entry
 * the SW serves first — makes the `<img>` fire `error`. Without a retry the tile
 * latches to the "No Image" placeholder and never recovers until a full reload
 * (which bypasses the SW). This hook retries a bounded number of times with
 * exponential backoff, appending a cache-busting query param each attempt so the
 * request *misses* the SW cache and re-pulls a fresh copy from the network rather
 * than getting the same broken response back.
 *
 * The retry budget is per-error-*burst*: a successful load (`onLoad`) resets the
 * counter, so a tile that recovers and then breaks again later in a long browse
 * session gets a fresh set of retries instead of stranding. The cache-bust token
 * is monotonic and never reused, so a reset can't re-request a URL that already
 * failed.
 *
 * To stop the retries from amplifying into a request storm across the
 * (virtualized, frequently-remounting) grid, two bounds apply:
 *  - **Offline**: when the browser is offline, errors latch straight to the
 *    fallback instead of retrying — the network can't help. Reconnecting clears
 *    suppression and re-attempts mounted tiles (see {@link onlineSubscribers}).
 *  - **Suppression memo**: a URL whose burst is exhausted (or that failed
 *    offline) is remembered for {@link RETRY_SUPPRESS_MS}, so remounting the
 *    same broken tile skips the burst rather than repeating it. See
 *    {@link suppressedUntil}.
 *
 * Recovery of a latched tile happens on a remount onto a non-suppressed URL, on
 * reconnect, or after the suppression TTL — so a transiently-broken-then-fixed
 * image can show the fallback for up to the TTL while online. That's the
 * deliberate trade for not re-requesting a known-broken URL on every remount.
 *
 * Returns the (possibly cache-busted) `src`, the `onError`/`onLoad` handlers to
 * wire to the `<img>`, and `failed` — true after the current burst's retries are
 * exhausted (or while the URL is suppressed), at which point the caller should
 * render its placeholder.
 */
export function useImageRetry(imageUrl: string | null | undefined, maxRetries = DEFAULT_MAX_RETRIES) {
  // The cache-bust value that drives `src`. 0 = pristine URL (happy path).
  const [token, setToken] = useState(0)
  // Start failed if this URL is already known-broken (e.g. a virtualized tile
  // scrolling back into view) so we don't re-issue a doomed request.
  const [failed, setFailed] = useState(() => isSuppressed(imageUrl))
  // Retries used in the current error burst; reset to 0 on a successful load.
  const burstRef = useRef(0)
  // Monotonic sequence for the cache-bust param — never reused, so every retry
  // is a guaranteed cache miss even after a burst reset.
  const seqRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // Mirror `failed` so the shared online-subscriber can tell whether this
  // instance needs to re-attempt without re-subscribing on every change.
  const failedRef = useRef(failed)
  failedRef.current = failed

  // Reset when the underlying image changes (e.g. a different card) and clear any
  // pending retry timer on unmount.
  useEffect(() => {
    burstRef.current = 0
    seqRef.current = 0
    setToken(0)
    setFailed(isSuppressed(imageUrl))
    return () => clearTimeout(timerRef.current)
  }, [imageUrl])

  // Re-attempt a latched tile when the browser comes back online. Only tiles
  // currently on the fallback reset (healthy ones must not re-request). One
  // shared module listener, so this is just a Set add/remove per instance.
  useEffect(() => {
    const reattempt = () => {
      if (!failedRef.current) return
      burstRef.current = 0
      setToken(0)
      setFailed(false)
    }
    onlineSubscribers.add(reattempt)
    return () => {
      onlineSubscribers.delete(reattempt)
    }
  }, [])

  const onError = useCallback(() => {
    // Don't retry when offline or once the budget is spent — latch to the
    // fallback and remember it so remounts (virtualized scroll) don't re-burst.
    if (isOffline() || burstRef.current >= maxRetries) {
      suppress(imageUrl)
      setFailed(true)
      return
    }
    burstRef.current += 1
    seqRef.current += 1
    const nextToken = seqRef.current
    const delay = RETRY_BASE_DELAY_MS * 2 ** (burstRef.current - 1)
    clearTimeout(timerRef.current)
    // Bumping the token changes `src`, forcing the <img> to re-request.
    timerRef.current = setTimeout(() => setToken(nextToken), delay)
  }, [maxRetries, imageUrl])

  const onLoad = useCallback(() => {
    // Success: refresh the retry budget for this tile and clear any suppression
    // so a later break gets a fresh set of retries. Keep the current token so we
    // don't reload the URL that just worked.
    burstRef.current = 0
    if (imageUrl) suppressedUntil.delete(imageUrl)
  }, [imageUrl])

  let src: string | null = null
  if (imageUrl) {
    src = token === 0 ? imageUrl : `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}__retry=${token}`
  }

  return { src, onError, onLoad, failed }
}
